import {
  ANALYTICS_CONTACTS_AMOUNT,
  ANALYTICS_TOP_DIALOGUES,
  BOTS,
  CONTACTS_BY_TYPE,
  CONTACTS_SOURCES_ANALYTICS,
  EXPORT_QA_KEYWORDS,
  EXPORT_UNANSWERED_QUESTIONS,
  QA_KEYWORDS,
  UNANSWERED_QUESTIONS,
  XRAY_ANALYTICS,
} from '@mm/api-routes';
import { KnownStatusType } from '@mm/components';
import * as queryString from 'query-string';
import { snake } from '../helpers/converterString';
import { Pagination } from '../store-interfaces';
import AuthenticatedController from './AuthenticatedController';

export const EMAIL_FILTER_TABS = [
  'automations',
  'sender_emails',
  'sender_domains',
  'by_automation',
  'by_widget',
  'summary',
] as const;

export type EmailFilterTabsType = typeof EMAIL_FILTER_TABS[number];
export type EmailAnalyticsParams = {
  botId: number;
  tab: EmailFilterTabsType;
  campaignId?: number;
  widgetDataEmailId?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
};
export type AnalyticsParams = {
  botId: number;
  startDate?: string;
  endDate?: string;
};

export const X_RAY_ANALYTICS_FILTER_TYPES = {
  contactsGathered: 'contacts_gathered',
  emailSent: 'email_sent',
  emailDomain: 'email_domain',
  wday: 'wday',
  topUrls: 'top_urls',
  timeOnSite: 'time_on_site',
  pageViews: 'page_views',
  clicks: 'clicks',
  webSessionsCount: 'web_sessions_count',
  avgLeadScores: 'avg_lead_scores',
  supressedContacts: 'supressed_contacts',
} as const;

type UIXRayAnalyticsFilterTypeTuple = typeof X_RAY_ANALYTICS_FILTER_TYPES;
export type UIXRayAnalyticsFilterType =
  UIXRayAnalyticsFilterTypeTuple[keyof UIXRayAnalyticsFilterTypeTuple];
export type XRayAnalyticsType = UIXRayAnalyticsFilterType;

type Email = string;
type Domain = string;
type Campaign = string;

export type EmailStatsEntry = Record<
  Email | Domain | Campaign,
  {
    sent?: number | null;
    open?: number | null;
    click?: number | null;
    leads?: number | null;
    remoteId?: string;
    email?: string;
    campaign?: string;
    campaignId?: number;
    senderEmail?: string;
    active?: boolean;
    status: KnownStatusType;
  }
>;

export type EmailStatsPayload = {
  stats: EmailStatsEntry[];
  pagination: Pagination;
};

export type XRayAnalyticsPayload = {
  dates?: string[];
  hits?: number[];
  total?: number;
  emailDomain?: string[];
  landingPageUrl?: string[];
  avgLeadScores?: {
    webPageViews?: null | number;
    scrollDepth?: null | number;
    numOfClicks?: null | number;
    timeOnSite?: null | number;
    supressedContacts?: null | number;
  };
  wday?: number[];
  views: string[];
  timeOnSite: string[];
  clicks: string[];
  webSessionsCount: string[];
  supressedContacts: {
    count: number;
  };
};

export default class BotAnalyticsController extends AuthenticatedController {
  static async getTopDialogues(
    botId: number,
    pageNumber?: number,
    pageSize?: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(
      ANALYTICS_TOP_DIALOGUES(botId, pageNumber, pageSize, startDate, endDate),
    );
  }
  static async getContactsAmountAnalytics(
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(ANALYTICS_CONTACTS_AMOUNT(botId, startDate, endDate));
  }
  static async getUnansweredQuestions(
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(UNANSWERED_QUESTIONS(botId, startDate, endDate));
  }
  static async getContactsByType(
    type: 'by_source' | 'by_platform' | 'by_platform_aggregated',
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(CONTACTS_BY_TYPE(type, botId, startDate, endDate));
  }
  static async exportUnansweredQuestions(
    botId: number,
    email: string,
    startDate?: string,
    endDate?: string,
  ) {
    return super.post(EXPORT_UNANSWERED_QUESTIONS(botId), {
      email,
      startDate,
      endDate,
    });
  }
  static async getQAKeywords(
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return super.get(QA_KEYWORDS(botId, startDate, endDate));
  }

  static async exportQAKeywords(
    botId: number,
    email: string,
    startDate?: string,
    endDate?: string,
  ) {
    return super.post(EXPORT_QA_KEYWORDS(botId), {
      email,
      startDate,
      endDate,
    });
  }

  static getEmailAnalytics(params: EmailAnalyticsParams) {
    const { botId } = params;
    const query = queryString.stringify(snake(params));
    return super.get(`${BOTS}/${botId}/analytics/emails?${query}`);
  }

  static getXRayAnalytics(type: XRayAnalyticsType, params: AnalyticsParams) {
    const { botId, startDate, endDate } = params;
    return super.get(XRAY_ANALYTICS(type, botId, startDate, endDate));
  }

  static getContactsSourcesAnalytics(botId: number) {
    return super.get(CONTACTS_SOURCES_ANALYTICS(botId));
  }
}
